import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import ChatMessage from "./ChatMessage";
import MessageInput from "./MessageInput";
import ChatTypingIndicator from "./ChatTypingIndicator";
import DataRail from "./DataRail";
import { openRail, railFor } from "./railState";
import { useRailOverlay } from "./useRailOverlay";
import { useRailWidth } from "./useRailWidth";
import ActivityTimeline from "./ActivityTimeline";
import axios from "axios";
import { useAgentConfig } from "../contexts/AgentConfigContext";
import { useAuth } from "../contexts/AuthContext";
import Dropdown from "../common_jsx/Dropdown";
import appConfig from "../config.js";
import { completionsPath } from "./completionsPath";
import SuggestionCards from "./SuggestionCards";
import { parseJSONResponse, isJSONResponse, processJSONContent, extractUnknownTypeInsights } from "../utils/responseParser";
import { mergeActivitySteps, applyStepsToMessage, liveActivityStepsFor } from "../utils/activitySteps";
import { createProgressPoller } from "../utils/progressPoller";
import { pollRunEvents } from "./pollRunEvents";
import { useDarkMode } from "../contexts/DarkModeContext";
import { extractSqlTableData, processAgentExecutionAnswer } from "../utils/tableParser";
import { loadSessionModes, loadLastUsedMode, saveLastUsedMode, saveSessionMode } from "../utils/sessionModeUtils";

// Constants
const DEFAULT_SESSION_TITLE = "New Session";

// Utility functions for date/time formatting
const formatDate = (timestamp) => {
  if (!timestamp) return "";
  // Ensure timestamp is a valid number
  const ts = Number(timestamp);
  if (isNaN(ts)) return "";
  
  // If timestamp is too large, it might be in milliseconds already
  const date = ts > 10000000000 ? new Date(ts) : new Date(ts * 1000);
  
  const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};


const isSameDay = (timestamp1, timestamp2) => {
  if (!timestamp1 || !timestamp2) return false;
  
  const ts1 = Number(timestamp1);
  const ts2 = Number(timestamp2);
  if (isNaN(ts1) || isNaN(ts2)) return false;
  
  const date1 = ts1 > 10000000000 ? new Date(ts1) : new Date(ts1 * 1000);
  const date2 = ts2 > 10000000000 ? new Date(ts2) : new Date(ts2 * 1000);
  
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// Date separator component (Google Chat style)
const DateSeparator = ({ date }) => (
  <div className="flex items-center justify-center my-4">
    <div className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-raised text-fg-muted">
      {date}
    </div>
  </div>
);

DateSeparator.propTypes = {
  date: PropTypes.string.isRequired,
};

function ChatScreen(props) {
  const { config, securityParams } = useAgentConfig();
  const { getUserIdDiscover, getAccessToken } = useAuth();
  const { isDarkMode } = useDarkMode();

  const [analysisMode, setAnalysisMode] = useState(() => loadLastUsedMode(null));

  // Restore analysis mode when active session changes or when sessionModes is updated (e.g. after messages load)
  useEffect(() => {
    if (props.activeChatSession) {
      // Prefer the prop (updated after messages load) over localStorage
      const modeFromProp = props.sessionModes?.[props.activeChatSession];
      if (modeFromProp) {
        setAnalysisMode(modeFromProp);
        // Keep lastUsedMode in sync so new sessions inherit the current session's mode
        saveLastUsedMode(config.agentId, modeFromProp);
      } else {
        const modes = loadSessionModes(config.agentId);
        const resolvedMode = modes[props.activeChatSession] || loadLastUsedMode(config.agentId);
        setAnalysisMode(resolvedMode);
        if (modes[props.activeChatSession]) {
          saveLastUsedMode(config.agentId, modes[props.activeChatSession]);
        }
      }
    } else {
      // New session: inherit the last used mode
      setAnalysisMode(loadLastUsedMode(config.agentId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activeChatSession, props.sessionModes, config.agentId]);

  // Wrapper: persist mode change to localStorage and notify parent
  const handleAnalysisModeChange = (mode) => {
    setAnalysisMode(mode);
    // Always persist last used mode so new sessions inherit it
    saveLastUsedMode(config.agentId, mode);
    if (props.activeChatSession && config.agentId) {
      saveSessionMode(config.agentId, props.activeChatSession, mode);
    }
    if (props.onSessionModeChange) {
      props.onSessionModeChange(props.activeChatSession, mode);
    }
  };
  const [inputText, setInputText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  // Steps for the turn currently in flight, read off the same bot message the
  // stream and the progress poller both write to. Empty once `answer_type`
  // lands, because from that point the bot card renders and owns the timeline —
  // this only covers the window where that card is deliberately hidden.
  const liveActivitySteps = useMemo(
    () => (isTyping ? liveActivityStepsFor(props.liveChat) : []),
    [isTyping, props.liveChat],
  );
  const [streamingStatus, setStreamingStatus] = useState('');
  const [quotedMessage, setQuotedMessage] = useState(null);
  const [, setCurrentParentId] = useState(null); // Removed unused currentParentId
  // Track which sessions currently have an active stream (Set supports concurrent streams)
  const activeStreamingSessionRef = useRef(new Set());
  const activeSessionRef = useRef(props.activeChatSession);
  // Track the latest partial bot message per session so it can be restored
  // when switching back to a session that is still streaming
  const inProgressMessagesRef = useRef(new Map()); // sessionId → bot message object
  const [dataSources, setDataSources] = useState([]);
  const [selectedDataSource, setSelectedDataSource] = useState([]);
  const [usedClarificationMessages, setUsedClarificationMessages] = useState(new Set());
  // Track sessions with finalized titles - persists across renders and prevents any title-related activity
  const sessionsWithFinalizedTitles = useRef(new Set());
  // Track title fetch attempts per session to prevent duplicate calls
  const [titleFetchAttempts, setTitleFetchAttempts] = useState(new Map());
  // Track timeout IDs for cleanup
  const pollingTimeoutsRef = useRef(new Map());
  // Live turn-progress pollers, keyed by session id. Kept separate from
  // pollingTimeoutsRef because that map is pruned by session id and would drop
  // these; also lets a session switch stop only its own poller.
  const progressPollersRef = useRef(new Map());
  // Track component mount status to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Ref to always have the latest chatSessions for polling
  const chatSessionsRef = useRef(props.chatSessions);
  // Track input text per session to prevent text from persisting across sessions
  const sessionInputTexts = useRef(new Map());
  // Guard against concurrent handleSend calls (race condition: user clicks send while
  // async session creation is in-flight — both find sessionId===null and create duplicates)
  const isHandlingSendRef = useRef(false);
  // Keep chatSessionsRef up to date with latest props.chatSessions
  useEffect(() => {
    chatSessionsRef.current = props.chatSessions;
  }, [props.chatSessions]);

  // Memoize apiConfig to prevent infinite re-renders
  // Removed unused apiConfig

  // Cleanup on unmount: clear all pending timeouts
  useEffect(() => {
    isMountedRef.current = true;
    // Captured for the cleanup closure rather than read from the ref at unmount,
    // which is the identity React actually wants stopped (and silences the
    // exhaustive-deps warning about reading .current in a cleanup).
    const pollers = progressPollersRef.current;

    return () => {
      isMountedRef.current = false;
      // Clear all pending timeouts
      pollingTimeoutsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      pollingTimeoutsRef.current.clear();
      pollers.forEach((poller) => poller.stop());
      pollers.clear();
    };
  }, []);

  // STRICT GUARD: Rebuild finalized titles set and prune tracking maps when sessions or active session changes
  useEffect(() => {
    // Build a set of current session IDs
    const currentSessionIds = new Set(props.chatSessions?.map(s => s.id) || []);

    // Rebuild finalized titles set from current sessions
    sessionsWithFinalizedTitles.current.clear();
    if (props.chatSessions && props.chatSessions.length > 0) {
      props.chatSessions.forEach(session => {
        if (session.summary && session.summary !== DEFAULT_SESSION_TITLE) {
          sessionsWithFinalizedTitles.current.add(session.id);
        }
      });
    }

    // Prune titleFetchAttempts for sessions no longer present
    setTitleFetchAttempts(prev => {
      const updated = new Map();
      for (const [id, val] of prev.entries()) {
        if (currentSessionIds.has(id)) updated.set(id, val);
      }
      return updated;
    });

    // Prune pollingTimeoutsRef for sessions no longer present
    pollingTimeoutsRef.current.forEach((timeoutId, id) => {
      if (!currentSessionIds.has(id)) {
        clearTimeout(timeoutId);
        pollingTimeoutsRef.current.delete(id);
      }
    });

    // If active session changes, reset title fetch attempts for previous session (if it doesn't have title)
    if (props.activeChatSession) {
      const activeSession = props.chatSessions?.find(s => s.id === props.activeChatSession);
      if (!activeSession?.summary || activeSession.summary === DEFAULT_SESSION_TITLE) {
        // Also reset title fetch attempts for this session so fetching can be retried
        setTitleFetchAttempts((prev) => {
          const updated = new Map(prev);
          updated.delete(props.activeChatSession);
          return updated;
        });
      }
    }
  }, [props.chatSessions, props.activeChatSession]);

  // Clear timeouts and update typing state when active session changes
  useEffect(() => {
    activeSessionRef.current = props.activeChatSession;
    // Clear all pending timeouts when session changes
    pollingTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pollingTimeoutsRef.current.clear();
    // Show typing indicator only if the new session has an active stream
    // Use Set.has() to support concurrent streams across multiple sessions
    setIsTyping(!!props.activeChatSession && activeStreamingSessionRef.current.has(props.activeChatSession));
  }, [props.activeChatSession]);

  // When messages finish loading for a session that still has an active stream,
  // inject the in-progress bot message back into liveChat so it appears immediately.
  // (The temp message was cleared when MainContent re-fetched from the API on switch-back.)
  useEffect(() => {
    if (
      !props.isLoadingMessages &&
      props.activeChatSession &&
      activeStreamingSessionRef.current.has(props.activeChatSession)
    ) {
      const inProgressMsg = inProgressMessagesRef.current.get(props.activeChatSession);
      if (inProgressMsg) {
        props.setLiveChat((prevMessages) => {
          const exists = prevMessages.some((m) => m.id === inProgressMsg.id);
          if (exists) return prevMessages;
          return [...prevMessages, inProgressMsg];
        });
      }
    }
  }, [props.isLoadingMessages, props.activeChatSession]);

  // Initialize usedClarificationMessages when liveChat changes (on session load)
  useEffect(() => {
    const messagesWithOptions = new Set();

    // Find the very last assistant message (author_id === 0)
    let lastAssistantMessage = null;
    for (let i = props.liveChat.length - 1; i >= 0; i--) {
      const msg = props.liveChat[i];
      if (msg.author_id === 0) {
        lastAssistantMessage = msg;
        break;
      }
    }

    // Disable ALL clarification options except those in the last assistant message
    props.liveChat.forEach((msg) => {
      if (msg.clarification_options && msg.clarification_options.length > 0) {
        // If this is NOT the last assistant message, disable its options
        if (!lastAssistantMessage || msg.id !== lastAssistantMessage.id) {
          messagesWithOptions.add(msg.id);
        }
        // If this IS the last assistant message, only enable if it has options
        else if (lastAssistantMessage && msg.id === lastAssistantMessage.id) {
          // Only enable if the last assistant message actually has clarification options
          if (!msg.clarification_options || msg.clarification_options.length === 0) {
            messagesWithOptions.add(msg.id);
          }
        }
      }
    });

    setUsedClarificationMessages(messagesWithOptions);
  }, [props.liveChat]);

  // Helper function to find which clarification option was selected
  const getSelectedOption = (messageId, clarificationOptions) => {
    if (!clarificationOptions || clarificationOptions.length === 0) {
      return null;
    }

    // Find the current message index
    const currentMessageIndex = props.liveChat.findIndex((msg) => msg.id === messageId);
    if (currentMessageIndex === -1) return null;

    // Look for the next user message after this bot message
    for (let i = currentMessageIndex + 1; i < props.liveChat.length; i++) {
      const nextMsg = props.liveChat[i];
      if (nextMsg.author_id !== 0) {
        // User message
        // Check if the user message text exactly matches any clarification option
        const selectedOption = clarificationOptions.find(
          (option) => nextMsg.message.trim().toLowerCase() === option.trim().toLowerCase()
        );
        return selectedOption || null;
      }
    }

    return null;
  };

  const inputTextArea = useRef(null);
  // Removed unused token
  const previousChatLength = useRef(props.liveChat.length);
  const shouldScrollRef = useRef(true);

  const handleSuggestionClick = (prompt) => {
    // Don't set inputText - just send the prompt directly
    handleSend(null, prompt);
  };

  // Restore input text when switching sessions
  useEffect(() => {
    if (props.activeChatSession) {
      // Restore saved input text for this session, or clear if none
      const savedText = sessionInputTexts.current.get(props.activeChatSession) || '';
      setInputText(savedText);
    } else {
      // No active session, clear input
      setInputText('');
    }
  }, [props.activeChatSession]);

  useEffect(() => {
    // Only scroll to bottom if:
    // 1. New messages were added (length increased), OR
    // 2. isTyping changed (bot is responding), OR
    // 3. shouldScrollRef is explicitly set to true
    const lengthIncreased = props.liveChat.length > previousChatLength.current;
    
    if (lengthIncreased || isTyping || shouldScrollRef.current) {
      scrollToBottom();
      shouldScrollRef.current = false; // Reset after scrolling
    }
    
    previousChatLength.current = props.liveChat.length;
  }, [props.liveChat, isTyping]);

  // The rail holds one slot, and only a press moves it: an answer arriving while
  // someone reads must not swap the rows out from under them. What that slot
  // MEANS is resolved on every render from the message array (see railState.js),
  // so a deleted turn closes the rail without an effect racing the render that
  // removed it.
  const [dataRail, setDataRail] = useState(null);
  const railView = railFor(props.liveChat, dataRail);
  const railIsOverlay = useRailOverlay();
  const [railWidth, setRailWidth] = useRailWidth();

  const openDataRail = useCallback((messageId, sourceKey) => {
    setDataRail((current) => {
      // A chip that reports itself pressed has to be able to un-press: pressing
      // the turn already on show closes the rail rather than reopening it.
      if (current && current.messageId === messageId && sourceKey === undefined) return null;
      return openRail(props.liveChat, messageId, sourceKey);
    });
  }, [props.liveChat]);

  const scrollToBottom = () => {
    const chatMessages = document.querySelector(".chat-messages");
    if (chatMessages) {
      chatMessages.scrollTop = chatMessages.scrollHeight;
    }
  };

  const resizeInput = () => {
    const elem = inputTextArea.current;
    if (elem) {
      elem.style.height = "58px";
      elem.style.height = `${elem.scrollHeight}px`;
    }
  };

  const handleInputChange = (event) => {
    const newText = event.target.value;
    setInputText(newText);
    // Save input text for current session
    if (props.activeChatSession) {
      sessionInputTexts.current.set(props.activeChatSession, newText);
    }
    resizeInput();
  };

  const pollSessionForTitle = async (sessionId, attemptNumber = 1, maxAttempts = 12) => {
    // Guard against unmounted component
    if (!isMountedRef.current) {
      return;
    }

    // STRICT GUARD: If session already has finalized title, abort immediately
    if (sessionsWithFinalizedTitles.current.has(sessionId)) {
      return;
    }
    
    // STRICT GUARD: Check current session state for title using latest ref
    const currentSession = chatSessionsRef.current?.find(s => s.id === sessionId);
    if (currentSession?.summary && currentSession.summary !== DEFAULT_SESSION_TITLE) {
      sessionsWithFinalizedTitles.current.add(sessionId);
      return;
    }
    
    
    try {
      // Fetch the specific session to check if title has been generated
      const response = await axios.get(
        `${config.apiUrl}/agents/${config.agentId}/sessions`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
          },
          params: {
            id: sessionId,
            discover_user_id: getUserIdDiscover(),
          },
        }
      );
      
      if (response.status === 200 && Array.isArray(response.data?.data)) {
        const session = response.data.data.find((s) => s && s.id === sessionId);
        const title = session?.title;
        // Check if backend has generated a title (not empty and not "New Session")
        if (title && title !== DEFAULT_SESSION_TITLE && title.trim() !== "") {
          // Guard against unmounted component
          if (!isMountedRef.current) {
            return;
          }

          // Update the session title in the state immediately
          props.setChatSessions((prevSessions) => {
            return prevSessions.map((s) => 
              s.id === sessionId 
                ? { ...s, summary: title } 
                : s
            );
          });
          
          // Mark session as finalized - NO MORE TITLE ACTIVITY FOR THIS SESSION
          sessionsWithFinalizedTitles.current.add(sessionId);
          
          // Mark as successfully fetched
          setTitleFetchAttempts(prev => {
            const updated = new Map(prev);
            updated.set(sessionId, 'success');
            return updated;
          });
          
          // Clear timeout from tracking
          pollingTimeoutsRef.current.delete(sessionId);
          
          return; // Success - stop polling
        }
      }
      
      // Title not ready yet, retry if we haven't reached max attempts
      if (attemptNumber < maxAttempts) {
        // Progressive delay: 2s, 3s, 4s, 5s, then 5s for remaining attempts
        const delayMs = Math.min(2000 + (attemptNumber * 1000), 5000);
        
        const timeoutId = setTimeout(() => {
          pollingTimeoutsRef.current.delete(sessionId);
          pollSessionForTitle(sessionId, attemptNumber + 1, maxAttempts);
        }, delayMs);
        
        // Track timeout for cleanup
        pollingTimeoutsRef.current.set(sessionId, timeoutId);
      } else {
        console.warn(`[Title Update] ⏱️ Polling timeout after ${maxAttempts} attempts for session:`, sessionId);
        // Guard against unmounted component
        if (!isMountedRef.current) {
          return;
        }
        // Mark as timed out
        setTitleFetchAttempts(prev => {
          const updated = new Map(prev);
          updated.set(sessionId, 'timeout');
          return updated;
        });
        // Clear timeout from tracking
        pollingTimeoutsRef.current.delete(sessionId);
      }
      
    } catch (error) {
      console.error(`[Title Update] ❌ Error on polling attempt ${attemptNumber}:`, error.message);
      
      // Retry on error if we haven't reached max attempts
      if (attemptNumber < maxAttempts) {
        const delayMs = 3000;
        
        const timeoutId = setTimeout(() => {
          pollingTimeoutsRef.current.delete(sessionId);
          pollSessionForTitle(sessionId, attemptNumber + 1, maxAttempts);
        }, delayMs);
        
        // Track timeout for cleanup
        pollingTimeoutsRef.current.set(sessionId, timeoutId);
      } else {
        console.error(`[Title Update] Failed after ${maxAttempts} attempts for session:`, sessionId);
        // Guard against unmounted component
        if (!isMountedRef.current) {
          return;
        }
        // Mark as failed
        setTitleFetchAttempts(prev => {
          const updated = new Map(prev);
          updated.set(sessionId, 'failed');
          return updated;
        });
        // Clear timeout from tracking
        pollingTimeoutsRef.current.delete(sessionId);
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedDataSource(selected);
  };

  // Helper function to check if response is valid for title generation
  const isValidResponseForTitle = (sessionState) => {
    // STRICT GUARD: Check if session already has finalized title
    const sessionId = sessionState.sessionId;
    if (sessionId && sessionsWithFinalizedTitles.current.has(sessionId)) {
      return false;
    }
    
    // Check 1: Must have executed source (unambiguous data)
    const hasExecutedSource = sessionState.hasExecutedSource;
    
    // Check 2: Must not have clarification options (no error/clarification)
    const hasClarifications = sessionState.hasClarificationOptions || false;
    
    // Check 3: Session must not already have a title
    const hasTitle = sessionState.sessionHasTitle;
    
    const isValid = hasExecutedSource && !hasClarifications && !hasTitle;
    
    return isValid;
  };

  const handleClarificationOptionClick = (option, messageId) => {
    // Only disable the current message if it's currently enabled
    // Don't interfere with the session-based disable logic
    setUsedClarificationMessages((prev) => new Set([...prev, messageId]));
    handleSend(null, option);
  };

  // Helper function to prepare request body
  const prepareRequestBody = (textToSend, messageId = null, sessionId = null) => {
    const requestBody = {
      // discover_user_id = Keycloak UUID for session ownership (immutable)
      // user_id defaults to Keycloak UUID, overridable by security params for RLS
      discover_user_id: getUserIdDiscover(),
      ...(securityParams || {}),
      question: textToSend,
      stream: true,
      session_id: sessionId || props.activeChatSession || null,
      user_email: props.user.email,
      user_name: props.user.name,
      message_id: messageId,
      analysis_mode: analysisMode,
    };

    if (selectedDataSource.length > 0) {
      requestBody.fulfillment_source_ids = selectedDataSource.map((source) => source.id);
    }

    return requestBody;
  };

  // Helper function to handle session creation
  const handleSessionCreation = (session_id, title, sessionState) => {
    if (!sessionState.sessionId && session_id) {
      sessionState.sessionId = session_id;

      if (!props.activeChatSession) {
        const sessionSummary = title || DEFAULT_SESSION_TITLE;
        const newChatSession = {
          id: session_id,
          summary: sessionSummary,
          hasUserMessages: true, // Session is being created because user sent a message
        };
        props.setChatSessions((prevSessions) => [...prevSessions, newChatSession]);
        props.setActiveChatSession(session_id);
        
        // Remove from empty sessions tracking since this session has a message
        if (props.emptySessionsRef) {
          props.emptySessionsRef.current.delete(session_id);
        }

        if (title) {
          sessionState.sessionHasTitle = true;
        }
      }
    } else if (session_id && sessionState.sessionId !== session_id) {
      // Backend returned a different session_id than expected
      // Update to use the backend's session_id
      sessionState.sessionId = session_id;
      
      // Check if this session already exists in our list
      const sessionExists = props.chatSessions?.some(s => s.id === session_id);
      
      if (!sessionExists) {
        const sessionSummary = title || DEFAULT_SESSION_TITLE;
        const newChatSession = {
          id: session_id,
          summary: sessionSummary,
          hasUserMessages: true,
        };
        props.setChatSessions((prevSessions) => [...prevSessions, newChatSession]);
      }
      
      // Remove from empty sessions tracking since this session has a message
      if (props.emptySessionsRef) {
        props.emptySessionsRef.current.delete(session_id);
      }
      
      // Ensure this session is active
      if (props.activeChatSession !== session_id) {
        props.setActiveChatSession(session_id);
      }

      if (title) {
        sessionState.sessionHasTitle = true;
      }
    }
  };

  // Helper function to handle session title updates
  const handleSessionTitleUpdate = (title, sessionId, sessionState) => {
    if (title && sessionId) {
      sessionState.sessionHasTitle = true;
      props.setChatSessions((prevSessions) => {
        return prevSessions.map((session) => (session.id === sessionId ? { ...session, summary: title } : session));
      });
    }
  };

  // Helper function to update bot message
  const updateBotMessage = (botMessageId, messageData, streamingSessionId) => {
    const {
      messageId,
      finalMessage,
      source,
      content_type,
      datatype,
      description,
      clarification_options,
      curl_commands,
      isComplete,
      total_rows,
      data_sources_map,
      reference,
      answer_type,
      reasoning_steps,
    } = messageData;

    props.setLiveChat((prevMessages) => {
      // Skip updates if the user is viewing a different session
      if (activeSessionRef.current !== streamingSessionId) {
        return prevMessages;
      }

      const buildUpdatedMsg = (msg) => {
        if (isComplete) {
          return {
            ...msg,
            id: messageId || botMessageId,
            // Stamped on completion, not when the placeholder went in. A turn
            // can run for minutes, and dating the answer to the moment the
            // question was asked made a reply that just landed read as though
            // it had been sitting there the whole time.
            created_at: Math.floor(Date.now() / 1000),
            message: finalMessage,
            source: source || msg.source,
            type: content_type || msg.type,
            datatype: datatype || msg.datatype,
            description: description || "",
            clarification_options: clarification_options || [],
            curl_commands: curl_commands || msg.curl_commands,
            total_rows: total_rows !== undefined ? total_rows : msg.total_rows,
            data_sources_map: data_sources_map || msg.data_sources_map,
            reference: reference || msg.reference,
            answer_type: answer_type || msg.answer_type,
            reasoning_steps: reasoning_steps || msg.reasoning_steps || null,
          };
        }
        return {
          ...msg,
          message: finalMessage,
          description: description !== undefined ? description : msg.description,
          total_rows: total_rows !== undefined ? total_rows : msg.total_rows,
          data_sources_map: data_sources_map || msg.data_sources_map,
          answer_type: answer_type || msg.answer_type,
          reasoning_steps: reasoning_steps || msg.reasoning_steps || null,
        };
      };

      // Try to find an existing bot message to update
      const botMsgIndex = prevMessages.findIndex(
        (msg) => (msg.id === botMessageId || (messageId && msg.id === messageId)) && msg.author_id === 0
      );

      if (botMsgIndex !== -1) {
        // Copy the array and update only the target index — avoids iterating the whole list
        const updated = [...prevMessages];
        updated[botMsgIndex] = buildUpdatedMsg(prevMessages[botMsgIndex]);
        return updated;
      }

      // Bot message not found (e.g., session was reloaded from API mid-stream).
      // Append it so the streaming answer appears alongside the user's question.
      const newBotMsg = buildUpdatedMsg({
        id: botMessageId,
        author_id: 0,
        message: "",
        type: "text",
        created_at: Math.floor(Date.now() / 1000),
      });
      return [...prevMessages, newBotMsg];
    });

    // Always keep in-progress ref current (partial and final) so switching back to a streaming
    // or just-completed session shows the latest content rather than the initial placeholder.
    const tracked = inProgressMessagesRef.current.get(streamingSessionId);
    if (tracked) {
      inProgressMessagesRef.current.set(streamingSessionId, {
        ...tracked,
        // Keep the restored copy's time in step with the one on screen.
        created_at: isComplete ? Math.floor(Date.now() / 1000) : tracked.created_at,
        message: finalMessage ?? tracked.message,
        description: description !== undefined ? description : tracked.description,
        answer_type: answer_type || tracked.answer_type,
        reasoning_steps: reasoning_steps || tracked.reasoning_steps || null,
        ...(isComplete && {
          id: messageId || tracked.id,
          source: source || tracked.source,
          type: content_type || tracked.type,
          datatype: datatype || tracked.datatype,
          clarification_options: clarification_options || tracked.clarification_options || [],
          curl_commands: curl_commands || tracked.curl_commands,
          reference: reference || tracked.reference,
        }),
      });
    }
  };

  /**
   * Merge polled activity steps into the in-flight bot message.
   *
   * Deliberately narrow: it touches reasoning_steps and nothing else, because it
   * runs concurrently with the stream's own updates and must not clobber a
   * message/description that has arrived in the meantime.
   */
  const applyPolledSteps = (botMessageId, streamingSessionId, sessionState, steps) => {
    if (!steps || steps.length === 0) return;

    props.setLiveChat((prevMessages) => {
      if (activeSessionRef.current !== streamingSessionId) return prevMessages;
      // Merged inside the updater, against the steps the message currently holds:
      // the stream writes this same field concurrently, so merging from a value
      // read beforehand would apply a stale snapshot and drop whatever the stream
      // committed in between. The merge dedupes, so a re-run is harmless.
      return applyStepsToMessage(prevMessages, {
        botMessageId,
        messageId: sessionState.messageId,
        steps,
      });
    });

    // The accumulator and the restore-on-session-switch copy are updated from the
    // same incoming steps rather than from the merge above (which happens later,
    // at commit time). Both merges dedupe, so the two paths converge.
    sessionState.reasoningSteps = mergeActivitySteps(sessionState.reasoningSteps, steps) || [];
    const tracked = inProgressMessagesRef.current.get(streamingSessionId);
    if (tracked) {
      inProgressMessagesRef.current.set(streamingSessionId, {
        ...tracked,
        reasoning_steps: mergeActivitySteps(tracked.reasoning_steps, steps) || [],
      });
    }
  };

  /**
   * Start polling for this turn's activity, for deployments whose SSE response is
   * buffered end to end (see utils/progressPoller). Costs nothing where streaming
   * works: the first delivered live step stops it, and the first request only
   * fires after a delay a working stream beats.
   */
  const startProgressPolling = (agentId, botMessageId, streamingSessionId, sessionState, token) => {
    if (!token || !config?.apiUrl) return null;

    // Replacing a tracked poller without stopping it would leak an untracked
    // request loop that no cleanup path can reach.
    stopProgressPolling(streamingSessionId);

    let loggedFailure = false;
    const poller = createProgressPoller({
      fetchProgress: async (since) => {
        const params = new URLSearchParams({
          message_id: token,
          discover_user_id: getUserIdDiscover() || "",
          since: String(since),
        });
        const res = await fetch(`${config.apiUrl}/agents/${agentId}/completions/progress?${params}`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        if (!res.ok) throw new Error(`progress ${res.status}`);
        const payload = await res.json();
        return payload?.data || null;
      },
      onSteps: (steps) => applyPolledSteps(botMessageId, streamingSessionId, sessionState, steps),
      shouldStop: () => !isMountedRef.current || !!sessionState.sawLiveStep,
      onError: (err) => {
        // Expected and harmless where the endpoint is not deployed yet: the
        // poller gives up on its own and the buffered response still carries the
        // answer. Logged once so a permanently broken endpoint is still visible.
        if (!loggedFailure) {
          loggedFailure = true;
          console.debug("[turn-progress] polling unavailable:", err?.message || err);
        }
      },
    });

    progressPollersRef.current.set(streamingSessionId, poller);
    poller.start();
    return poller;
  };

  const stopProgressPolling = (streamingSessionId) => {
    const poller = progressPollersRef.current.get(streamingSessionId);
    if (poller) {
      poller.stop();
      progressPollersRef.current.delete(streamingSessionId);
    }
  };

  // Helper function to process streaming data
  const processStreamingData = (parsedData, botMessageId, sessionState) => {
    const {
      answer,
      id,
      session_id,
      executed_queries,
      executed_query,
      curl_commands,
      content_type,
      title,
      running_status,
      description,
      clarification_options,
      total_count,
      partial,
      reference,
      answer_type,
      reasoning_steps,
      reasoning_step,
    } = parsedData.data;

    let source = "";
    let datatype = null;
    let finalMessage = answer;
    let finalDescription = description;
    let dataSourcesMap = null;
    let processedJSONContent = null;

    // Track if response has clarification options (indicates ambiguous/error response)
    if (clarification_options && clarification_options.length > 0) {
      sessionState.hasClarificationOptions = true;
    }

    // Determine if this is a valid response for title generation
    // Valid responses include:
    // 1. SQL responses: executed_queries or executed_query
    // 2. API responses: curl_commands (API sub-agent calls)
    // 3. Table content type (SQL results)
    // 4. Non-ambiguous responses with actual data (Master Agent insights)
    let hasValidData = false;
    
    // Check for SQL queries
    if (executed_queries && executed_queries.length > 0) {
      source = executed_queries;
      hasValidData = true;
    } else if (executed_query) {
      source = executed_query;
      hasValidData = true;
    }
    
    // Check for API calls (curl_commands from API sub-agents)
    if (curl_commands && curl_commands.length > 0) {
      hasValidData = true;
    }
    
    // Check for table content type (indicates successful data retrieval)
    if (content_type === "table") {
      hasValidData = true;
    }
    
    // Check if answer contains structured data (insights, recommendations, etc.)
    if (answer && isJSONResponse(answer)) {
      try {
        const parsed = typeof answer === 'string' ? JSON.parse(answer) : answer;
        // Check for Master Agent response with insights (not ambiguous)
        if (!parsed.is_ambiguous && (parsed.key_insights || parsed.actionable_recommendations || parsed.data_sources)) {
          hasValidData = true;
        }
      } catch (e) {
        console.error("Failed to parse bot message as JSON:", e);
      }
    }
    
    // Only mark as having executed source if we have valid data AND no clarifications
    if (hasValidData && !sessionState.hasClarificationOptions) {
      sessionState.hasExecutedSource = true;
    }

    datatype = content_type === "table" ? "sql" : "text";

    // Check if the answer is a JSON response that should be parsed
    if (answer && isJSONResponse(answer)) {
      // Track the last insights JSON for post-stream finalization (fixes stuck
      // "Verifying results..." and raw-JSON display after running_status:true final chunks)
      sessionState.lastInsightsAnswer = answer;
      processedJSONContent = processJSONContent(
        answer,
        !!(clarification_options && clarification_options.length > 0)
      );
      if (processedJSONContent) {
        if (processedJSONContent.datatype !== null) datatype = processedJSONContent.datatype;
        finalMessage = processedJSONContent.finalMessage;
        if (processedJSONContent.finalDescription !== null) finalDescription = processedJSONContent.finalDescription;
        if (processedJSONContent.dataSources) dataSourcesMap = processedJSONContent.dataSources;
      }
      // For unknown answer_type: show only key_insights as plain message, skip recommendations
      // (recommendations may contain internal agent IDs not suitable for end users)
      if (answer_type === 'unknown') {
        const insightsText = extractUnknownTypeInsights(answer);
        if (insightsText !== null) {
          finalMessage = insightsText;
          finalDescription = '';
          datatype = 'text';
        }
      }
      // Partial response: data arrived but insights are still being generated
      if (partial === true && (!finalDescription || finalDescription.trim() === '')) {
        finalDescription = '\u2728 Generating insights...';
      }
    } else if (content_type === "table" && answer) {
      finalMessage = extractSqlTableData(answer);
    } else if (answer) {
      // Handle agent execution records: JSON array where each element has an 'answer' field
      const agentResult = processAgentExecutionAnswer(answer);
      if (agentResult !== null) {
        finalMessage = agentResult.answer;
        // If the extracted answer is a markdown table, treat it as SQL data
        if (agentResult.isSql) {
          datatype = "sql";
        }
      }
    }

    // Determine completion status here so it can be used in the citation-strip guard below.
    const isComplete = running_status !== true || content_type === "table";

    // ANY event arriving while the turn is still running proves this deployment
    // forwards the stream rather than buffering it, so the progress poller
    // (started as a fallback) can stand down — whether or not the event carried a
    // typed step. Keyed on mid-turn arrival, not on step shape, so an older
    // backend sending only untyped status text stands the poller down too.
    if (!isComplete) {
      sessionState.sawLiveStep = true;
    }

    // During streaming: strip RAG citation markers (##i$$) to avoid showing raw tokens mid-stream.
    // When complete: preserve them so ChatMessage can render them as inline [N] citations.
    if (!isComplete && finalMessage && typeof finalMessage === 'string' && !isJSONResponse(finalMessage)) {
      finalMessage = finalMessage.replace(/ *(#{2}\d+\$+) */g, ' ');
    }

    // Save data_sources table data to prevent subsequent status messages from overwriting.
    // Only save when the answer actually contains data_sources with real table content.
    if (answer && isJSONResponse(answer) && finalMessage && finalMessage.trim()) {
      let answerObj = answer;
      if (typeof answer === 'string') {
        try { answerObj = JSON.parse(answer); } catch { answerObj = null; }
      }
      if (answerObj && answerObj.data_sources && Object.keys(answerObj.data_sources).length > 0) {
        sessionState.partialTableData = finalMessage;
        sessionState.partialDatatype = datatype;
        if (!dataSourcesMap) dataSourcesMap = answerObj.data_sources;
      }
    }

    handleSessionCreation(session_id, title, sessionState);
    handleSessionTitleUpdate(title, sessionState.sessionId, sessionState);

    if (id) {
      sessionState.messageId = id;
    }
    // Capture answer_type once it arrives (first non-null value wins)
    if (answer_type && !sessionState.answerType) {
      sessionState.answerType = answer_type;
    }
    // The final event carries the authoritative TYPED reasoning-step list — capture
    // it unconditionally (the final chunk often has running_status:true, so isComplete
    // is false here) so it replaces the untyped live-accumulated steps.
    if (Array.isArray(reasoning_steps)) {
      sessionState.finalReasoningSteps = reasoning_steps;
    }
    // Accumulate description whenever a meaningful value arrives, not just on completion.
    // In fast mode the summary chunk (running_status:true, no content_type:"table") has
    // isComplete=false, so the old isComplete-only guard discarded it; the subsequent table
    // chunk then cleared description to "" because sessionState.finalDescription was undefined.
    if (finalDescription != null && finalDescription !== '') {
      sessionState.finalDescription = finalDescription;
    }
    if (isComplete) {
      // JSON-derived content that was already captured for data-returning answer types.
      const skipMessageUpdate = !isJSONResponse(answer) &&
        sessionState.lastInsightsAnswer &&
        ['rag', 'sql', 'api', 'mixed'].includes(sessionState.answerType);
      if (!skipMessageUpdate) {
        sessionState.finalMessage = finalMessage ?? sessionState.finalMessage;
      }
      // Capture reference on completion. RAG answers populate chunks/doc_aggs;
      // Gemini google_search grounding populates web_sources / search_entry_point
      // with no RAG chunks. Without the latter, web-grounded citations would
      // render only after a page reload (when the persisted message is refetched).
      if (
        reference?.chunks?.length > 0 ||
        reference?.doc_aggs?.length > 0 ||
        reference?.web_sources?.length > 0 ||
        reference?.search_entry_point
      ) {
        sessionState.finalReference = reference;
      }
    }

    // During streaming: SQL/table data goes to the Data tab; status/text goes to both tabs.
    // The post-stream flush will split Data/Insights content correctly after completion.
    const isTableLike = content_type === "table" || datatype === "sql";
    let streamingMessage, streamingDescription;

    // If we already have partial data from data_sources and this is just a status update
    // (running_status: true, plain text answer), preserve the table in the data tab
    if (!isComplete && !isTableLike && !isJSONResponse(answer) && sessionState.partialTableData) {
      streamingMessage = sessionState.partialTableData;
      streamingDescription = typeof answer === 'string' ? answer : finalDescription;
      datatype = sessionState.partialDatatype || datatype;
    } else if (processedJSONContent) {
      // JSON response was successfully parsed — use the split finalMessage/finalDescription
      // directly instead of the raw answer object (which would render HTML tags as plain text)
      streamingMessage = finalMessage ?? '';
      streamingDescription = finalDescription ?? '';
    } else {
      streamingMessage = isTableLike ? finalMessage : answer;
      streamingDescription = !isTableLike ? answer : finalDescription;
    }

    // Accumulate the live activity timeline. The backend types every
    // running_status event (thought / tool / status) and ships it as
    // `reasoning_step`; older backends send only the status text, so fall back to
    // an untyped status step rather than showing nothing while the turn runs.
    // Either way the final typed list (finalReasoningSteps) supersedes these.
    const liveStep = reasoning_step && reasoning_step.content
      ? reasoning_step
      : (!isComplete && answer && typeof answer === 'string' && !isJSONResponse(answer) && content_type !== 'table'
        ? { type: 'status', content: answer, ts: Date.now() / 1000 }
        : null);
    if (liveStep) {
      const steps = sessionState.reasoningSteps;
      const last = steps[steps.length - 1];
      if (!last || last.content !== liveStep.content || last.type !== liveStep.type) {
        steps.push(liveStep);
      }
    }

    // Steps salvaged out of leaked scaffolding (see utils/agentScaffolding).
    // They are the only trail that turn has, since a producer that leaks its
    // transcript never emitted typed steps for it.
    if (processedJSONContent?.activitySteps?.length) {
      sessionState.salvagedSteps = processedJSONContent.activitySteps;
    }

    updateBotMessage(botMessageId, {
      messageId: sessionState.messageId,
      finalMessage: isComplete ? sessionState.finalMessage : streamingMessage,
      source,
      content_type,
      datatype,
      description: isComplete ? sessionState.finalDescription : streamingDescription,
      clarification_options,
      curl_commands,
      isComplete,
      total_rows: total_count,
      data_sources_map: dataSourcesMap,
      reference: isComplete ? sessionState.finalReference : undefined,
      answer_type: sessionState.answerType,
      reasoning_steps: mergeActivitySteps(
        sessionState.finalReasoningSteps || sessionState.reasoningSteps,
        sessionState.salvagedSteps,
      ),
    }, sessionState.sessionId);

    // Update streaming status for the typing indicator.
    // Once answer_type has arrived (partial data shown), always show "Generating insights..."
    // regardless of isComplete — subsequent chunks like "Step completed" should not override it.
    // Only pre-answer_type status messages (Analyzing..., Sub-agent is running...) flow through.
    if (sessionState.answerType) {
      setStreamingStatus('\u2728 Generating insights...');
    } else if (!isComplete && answer && typeof answer === 'string' && !isJSONResponse(answer) && content_type !== 'table') {
      setStreamingStatus(answer);
    }
  };

  // Helper function to process stream chunks
  const processStreamChunks = async (reader, decoder, botMessageId, sessionState) => {
    let buffer = "";
    let terminated = false;

    while (true) {
      let readDone, value;
      try {
        ({ done: readDone, value } = await reader.read());
      } catch (e) {
        console.error('Stream read error:', e);
        break;
      }

      if (value) {
        buffer += decoder.decode(value, { stream: true });
      }

      // Process every complete line in the buffer.  Each SSE event is a single
      // line: "data:{json}\n".  We split on \n so we handle both \n-separated and
      // \n\n-separated backends without merging multiple events into one string
      // (which broke JSON.parse when a second "data:" prefix appeared mid-string).
      // .trim() on each line also tolerates \r\n line endings.
      let newlineIdx;
      while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIdx);
        buffer = buffer.slice(newlineIdx + 1);

        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data:')) continue;

        const dataContent = trimmed.slice(5).trim();
        if (!dataContent) continue;

        try {
          const parsedData = JSON.parse(dataContent);
          if (parsedData.data === true) {
            terminated = true;
            break;
          }
          processStreamingData(parsedData, botMessageId, sessionState);
        } catch (e) {
          console.warn('SSE chunk parse error (skipping):', e.message);
        }
      }

      if (terminated || readDone) break;
    }

    // Flush decoder and process any remaining buffered data (stream ended mid-event)
    buffer += decoder.decode();
    const remaining = buffer.trim();
    if (remaining && remaining.startsWith('data:')) {
      const dataContent = remaining.slice(5).trim();
      if (dataContent) {
        try {
          const parsedData = JSON.parse(dataContent);
          if (parsedData.data !== true) {
            processStreamingData(parsedData, botMessageId, sessionState);
          }
        } catch (e) {
          console.error("Error parsing final streaming chunk:", e);
        }
      }
    }
  };

  const handleSend = async (event, predefinedText = null) => {
    const textToSend = predefinedText || inputText;
    if (!textToSend || !config.agentId) {
      return;
    }

    // Prevent concurrent sends: during async session creation React state hasn't
    // updated yet, so a second click also finds sessionId===null and creates a
    // duplicate session + completion. This ref guard is synchronous.
    if (isHandlingSendRef.current) return;
    isHandlingSendRef.current = true;

    // CRITICAL FIX: If no active session exists, create one proactively before sending the message
    // This prevents backend errors when trying to access an empty session list
    let sessionIdToUse = props.activeChatSession;
    
    if (!sessionIdToUse) {
      try {
        const agentId = config.agentId;
        const createSessionResponse = await axios.post(
          `${config.apiUrl}/agents/${agentId}/sessions`,
          { ...(securityParams || {}), discover_user_id: getUserIdDiscover() },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (createSessionResponse.data && createSessionResponse.data.data) {
          sessionIdToUse = createSessionResponse.data.data.id;
          
          // Add the new session to the list and set it as active
          const newChatSession = {
            id: sessionIdToUse,
            summary: DEFAULT_SESSION_TITLE,
            update_time: Date.now(),
            hasUserMessages: false, // Will be set to true after message is sent
          };
          
          props.setChatSessions((prevSessions) => [...prevSessions, newChatSession]);
          
          // Mark this session as newly created to prevent message fetch race condition
          if (props.newlyCreatedSessionsRef) {
            props.newlyCreatedSessionsRef.current.add(sessionIdToUse);
          }
          
          // Mark as empty initially - will be removed after message is sent
          if (props.emptySessionsRef) {
            props.emptySessionsRef.current.add(sessionIdToUse);
          }
          
          // Save mode for newly created session BEFORE setActiveChatSession triggers the effect
          saveSessionMode(config.agentId, sessionIdToUse, analysisMode);
          if (props.onSessionModeChange) {
            props.onSessionModeChange(sessionIdToUse, analysisMode);
          }

          props.setActiveChatSession(sessionIdToUse);
        } else {
          // 2xx but body had no session id — treat as a failure to avoid ghost session
          throw new Error(
            createSessionResponse.data?.message ||
            createSessionResponse.data?.data?.message ||
            'Session creation returned no session ID'
          );
        }
      } catch (error) {
        console.error("Error creating session before first message:", error);
        // Session creation failed — stop here. Never proceed with session_id: null;
        // that causes the backend to create a ghost session which appears in the sidebar
        // and runs the completion a second time.
        const errMsg =
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          error.message ||
          "Failed to start session. Please try again.";
        // Add the user's message to liveChat before the error so the error
        // doesn't land at index 0. The visibleMessages filter removes the first
        // bot message (index === 0, author_id === 0) to hide the default greeting,
        // which would also swallow this error if it lands at index 0.
        props.setLiveChat((prev) => [
          ...prev,
          {
            id: props.user.id + Date.now().toString(),
            message: textToSend,
            author_id: props.user.id,
            created_at: Math.floor(Date.now() / 1000),
          },
        ]);
        handleSendError(new Error(errMsg));
        isHandlingSendRef.current = false;
        return;
      }
    }

    // Save current mode for existing sessions (new sessions already saved above)
    else if (sessionIdToUse && config.agentId) {
      saveSessionMode(config.agentId, sessionIdToUse, analysisMode);
      if (props.onSessionModeChange) {
        props.onSessionModeChange(sessionIdToUse, analysisMode);
      }
    }

    const tempChatId = props.user.id + Date.now().toString();
    const currentTimestamp = Math.floor(Date.now() / 1000); // Current time in seconds
    const userMessage = {
      id: tempChatId,
      message: textToSend,
      author_id: props.user.id,
      created_at: currentTimestamp, // Add timestamp
    };

    props.setLiveChat((prevMessage) => [...prevMessage, userMessage]);

    if (sessionIdToUse) {
      props.updateSessionTimestamp(sessionIdToUse);
      // Mark session as having user messages
      props.setChatSessions((prevSessions) => 
        prevSessions.map(session => 
          session.id === sessionIdToUse 
            ? { ...session, hasUserMessages: true }
            : session
        )
      );
      // Remove from empty sessions tracking since user sent a message
      if (props.emptySessionsRef) {
        props.emptySessionsRef.current.delete(sessionIdToUse);
      }
    }

    setIsTyping(true);
    if (!predefinedText) {
      setInputText("");
      // Clear saved input text for current session after sending
      if (sessionIdToUse) {
        sessionInputTexts.current.delete(sessionIdToUse);
      }
    }
    setQuotedMessage(null);
    setCurrentParentId(null);
    resizeInput();

    // Release the send guard now that session creation is done.
    // The guard only needs to block the brief async session-creation window; holding it
    // for the full stream duration prevents sending in other sessions concurrently.
    isHandlingSendRef.current = false;

    const agentId = config.agentId;
    // Use the created or existing session ID
    const requestBody = prepareRequestBody(textToSend, tempChatId, sessionIdToUse);

    try {
      const botMessageId = `bot-${Date.now()}`;
      const botTimestamp = Math.floor(Date.now() / 1000); // Current time in seconds
      const botPlaceholder = {
        id: botMessageId,
        author_id: 0,
        message: "Processing your request...",
        type: "text",
        created_at: botTimestamp,
        analysis_mode: analysisMode,
      };
      props.setLiveChat((prevMessage) => [...prevMessage, botPlaceholder]);
      // Track so we can restore it if user switches sessions mid-stream
      inProgressMessagesRef.current.set(sessionIdToUse, { ...botPlaceholder });

      activeStreamingSessionRef.current.add(sessionIdToUse);
      if (props.onStreamStart) props.onStreamStart(sessionIdToUse);

      // Check if the current session already has a title
      // Use sessionIdToUse since it might have been just created
      const currentSession = sessionIdToUse 
        ? props.chatSessions?.find(s => s.id === sessionIdToUse)
        : null;
      const currentSessionHasTitle = currentSession 
        ? (currentSession.summary && currentSession.summary !== DEFAULT_SESSION_TITLE)
        : false;

      const sessionState = {
        sessionId: sessionIdToUse,
        finalMessage: "",
        messageId: "",
        sessionHasTitle: currentSessionHasTitle,
        hasExecutedSource: false,
        hasClarificationOptions: false,
        partialTableData: null,
        partialDatatype: null,
        answerType: null,
        reasoningSteps: [],
        finalReasoningSteps: null,
        salvagedSteps: null,
        sawLiveStep: false,
      };

      // Polling starts BEFORE the request is awaited, and that ordering is the
      // whole point. A hop that buffers the response cannot send headers before
      // it has the body, so `await fetch` does not resolve until the turn is
      // already over — starting the poller after it meant the first tick found a
      // finished turn and stood down without ever issuing a request. Everything
      // it needs (the correlation token, the session, the placeholder id) is
      // known here, so there is no reason to wait.
      startProgressPolling(agentId, botMessageId, sessionIdToUse, sessionState, tempChatId);

      try {
        const response = await fetch(`${config.apiUrl}/agents/${agentId}/${completionsPath()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify(requestBody),
        });

        const contentType = (response.headers.get("content-type") || "").toLowerCase();
        if (contentType.includes("application/json")) {
          // Polling transport (discover PR #1859): the POST above returns
          // {run_id} JSON instead of an SSE stream when the agent's dsl
          // response transport is "polling" — read the run's event log
          // instead of the response body.
          const payload = await response.json();
          const runId = payload?.data?.run_id;
          if (payload?.code === 0 && runId) {
            await pollRunEvents({
              eventsUrl: `${config.apiUrl}/agents/${agentId}/runs/${runId}/events`,
              getHeaders: () => ({ Authorization: `Bearer ${getAccessToken()}` }),
              onEnvelope: (envelope) => {
                if (envelope?.data === true) return;
                processStreamingData(envelope, botMessageId, sessionState);
              },
            });
          } else {
            // Non-zero code straight from the POST — surface it exactly like a
            // code:500 SSE chunk would be (processStreamingData never reads
            // `code`, only `data.answer` / `data.reference`).
            // FastAPI errors (e.g. from the chat backend) carry the reason in `detail`.
            const detail = typeof payload?.detail === "string" ? payload.detail : "";
            const errorMessage = payload?.message || detail || "Request failed";
            processStreamingData(
              {
                code: 500,
                message: errorMessage,
                data: { answer: `**ERROR**: ${errorMessage}`, reference: [] },
              },
              botMessageId,
              sessionState,
            );
          }
        } else {
          if (!response.ok) {
            // A non-JSON error (e.g. a gateway's HTML error page) has no SSE lines:
            // without this the reply would sit on "Processing your request..." with no
            // error shown. JSON errors are handled in the branch above.
            response.body?.cancel().catch(() => {});
            throw new Error(`Request failed (${response.status})`);
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          await processStreamChunks(reader, decoder, botMessageId, sessionState);
        }
      } finally {
        // The stream is authoritative for the final answer; once it is done the
        // poller has nothing left to contribute. In the finally so a failed fetch
        // cannot leave it running either.
        stopProgressPolling(sessionIdToUse);
      }

      // Post-stream finalization: the last streaming chunk (running_status: true) may have
      // overwritten msg.description with a status message ("Verifying results...") or raw JSON,
      // or the final complete data may have arrived with running_status: true (so isComplete was
      // never set). Re-process the last insights answer and apply the correct final state.
      if (sessionState.lastInsightsAnswer) {
        const processed = processJSONContent(
          sessionState.lastInsightsAnswer,
          sessionState.hasClarificationOptions
        );
        if (processed) {
          let flushDesc = processed.finalDescription || sessionState.finalDescription;
          let flushMsg = processed.finalMessage;
          let flushDtype = processed.datatype;
          // For unknown: re-apply key_insights override so flush doesn't reset to blank
          if (sessionState.answerType === 'unknown') {
            const insightsText = extractUnknownTypeInsights(sessionState.lastInsightsAnswer);
            if (insightsText !== null) {
              flushMsg = insightsText;
              flushDesc = '';
            }
          }
          // Only apply if the user is still viewing this session
          if (activeSessionRef.current === sessionIdToUse) {
            props.setLiveChat((prevMessages) =>
              prevMessages.map((msg) => {
                if (msg.id !== botMessageId && msg.id !== sessionState.messageId) return msg;
                if (msg.author_id !== 0) return msg;
                return {
                  ...msg,
                  message: flushMsg,
                  description: flushDesc || "",
                  ...(flushDtype && flushDtype !== null && { datatype: flushDtype }),
                  ...(sessionState.finalReasoningSteps && { reasoning_steps: sessionState.finalReasoningSteps }),
                };
              })
            );
          }
        }
      }

      // Determine the session ID to check
      const sessionIdToCheck = sessionState.sessionId || props.activeChatSession;
      
      // Check if we should trigger title update
      if (sessionIdToCheck) {
        // STRICT GUARD: Abort if session has finalized title
        if (sessionsWithFinalizedTitles.current.has(sessionIdToCheck)) {
          return;
        }
        
        // Guard: Block if we've already attempted or are currently attempting to fetch title for this session
        const fetchStatus = titleFetchAttempts.get(sessionIdToCheck);
        // Treat 'success', 'failed', 'attempting', and 'timeout' as terminal/blocked states
        const alreadyAttempted = (
          fetchStatus === 'success' ||
          fetchStatus === 'failed' ||
          fetchStatus === 'attempting' ||
          fetchStatus === 'timeout'
        );

        if (!alreadyAttempted && isValidResponseForTitle(sessionState)) {
          // Mark as attempting to prevent duplicate calls
          setTitleFetchAttempts(prev => {
            const updated = new Map(prev);
            updated.set(sessionIdToCheck, 'attempting');
            return updated;
          });

          // Initial delay to allow backend to start processing (backend takes 10–15s)
          const timeoutId = setTimeout(() => {
            pollingTimeoutsRef.current.delete(sessionIdToCheck);
            pollSessionForTitle(sessionIdToCheck, 1, 12);
          }, 3000); // 3s initial delay before first poll
          
          // Track timeout for cleanup
          pollingTimeoutsRef.current.set(sessionIdToCheck, timeoutId);
        }
      }
    } catch (error) {
      handleSendError(error);
    } finally {
      // Clean up using the local sessionIdToUse (not the ref, which may now point to a different
      // concurrent session that started streaming after this one).
      inProgressMessagesRef.current.delete(sessionIdToUse);
      activeStreamingSessionRef.current.delete(sessionIdToUse);
      // Only hide the typing indicator if the currently visible session is no longer streaming.
      // If another concurrent session is still active for the current view, leave isTyping true.
      if (!activeStreamingSessionRef.current.has(activeSessionRef.current)) {
        setIsTyping(false);
        setStreamingStatus('');
      }
      isHandlingSendRef.current = false;
      if (props.onStreamEnd) props.onStreamEnd(sessionIdToUse, textToSend);
    }
  };

  function handleSendError(error) {
    console.error(error);
    props.setLiveChat((prevChat) => {
      // Remove the "Processing your request..." placeholder, then append the error
      const filtered = prevChat.filter(m => m.message !== "Processing your request...");
      return [...filtered, {
        id: `error-${Date.now()}`,
        author_id: 0,
        message: `⚠️ ${error.message || "Something went wrong. Please try again."}`,
        type: "text",
        created_at: Math.floor(Date.now() / 1000),
      }];
    });
  }

  const handleInputKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!isTyping) {
        handleSend();
      }
    }
    if (event.key === "ArrowUp" && inputText === "") {
      event.preventDefault();
      for (let i = props.liveChat.length - 1; i >= 0; i--) {
        if (props.liveChat[i].author_id === props.user.id) {
          setInputText(props.liveChat[i].message);
          break;
        }
      }
    }
  };

  const loadAddedFulfillmentSources = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/datasets`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      if (response.status === 200) {
        setDataSources(response.data);
        if (response.data.length <= 1) {
          setSelectedDataSource([response.data[0]]);
        }
      }
    } catch (error) {
      console.error("Error fetching fulfillment sources:", error);
    }
  };

  useEffect(() => {
    loadAddedFulfillmentSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.apiUrl]);

  // Filter out the first bot message (default greeting) for rendering purposes.
  // Keep error messages (isError: true) even when they land at index 0 — they are
  // synthesised by the app (not a backend greeting) and must always be shown.
  const visibleMessages = props.liveChat.filter((msg, index) => {
    return !(index === 0 && msg.author_id === 0 && !msg.isError);
  });

  // Determine if we should show the welcome screen (no scrolling needed)
  // Show welcome screen when there are no visible messages (empty or only the default bot greeting)
  const isWelcomeScreen = !props.isLoadingMessages && visibleMessages.length === 0;

  const messageInputArea = (
    <div className="flex flex-col w-full gap-2">
      {props.sessionCreationError && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl text-sm bg-danger-soft border border-danger text-danger">
          <span className="mt-0.5 shrink-0">&#x26a0;&#xfe0f;</span>
          <span>{props.sessionCreationError}</span>
        </div>
      )}
      {props.missingSessionParams && props.missingSessionParams.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-xl text-sm bg-warning-soft border border-warning text-warning">
          <span className="mt-0.5 shrink-0">&#x26a0;&#xfe0f;</span>
          <span>
            Required security {props.missingSessionParams.length === 1 ? 'param' : 'params'} not set:{' '}
            <strong>{props.missingSessionParams.join(', ')}</strong>.{' '}
            Open the <strong>Security Params</strong> panel (lock icon in the top bar) to set{' '}
            {props.missingSessionParams.length === 1 ? 'it' : 'them'}.
          </span>
        </div>
      )}
      {dataSources.length > 1 && (
        <div className="mr-3">
          <Dropdown items={dataSources} type="checklist" onSelectionChange={handleSelectionChange} />
        </div>
      )}
      <MessageInput
        value={inputText}
        setInputText={setInputText}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyPress}
        onClick={handleSend}
        quotedMessage={quotedMessage}
        setQuotedMessage={setQuotedMessage}
        setCurrentParentId={setCurrentParentId}
        isTyping={isTyping}
        analysisMode={analysisMode}
        setAnalysisMode={handleAnalysisModeChange}
      />
    </div>
  );

  return (
    <div
      className="chat-stage"
      data-rail={railView ? 'open' : 'closed'}
      style={{ '--chat-rail': `${railWidth}px` }}
    >
    <div 
      className={`chat-messages flex flex-col-reverse ${isWelcomeScreen ? 'overflow-hidden relative' : ''}`}
      onScroll={props.handleScroll}
    >
      {/* First in the DOM, and therefore at the BOTTOM: this container is
          `flex-col-reverse`, which is also what keeps the thread pinned to the
          latest turn. Sticky rather than fixed, so it inherits the column's
          width from the track it sits in instead of measuring the sidebar, and
          the rail can never cover it.

          bg-app, and the fade strip above it, are what stop the transcript from
          reading through the composer: --bg-surface is deliberately translucent
          in several themes, so with nothing but the scroll track behind it the
          message passing underneath showed through the input and collided with
          the placeholder. An opaque layer here composites that translucency
          against a constant colour instead of against moving text. */}
      {!isWelcomeScreen && (
        <div className="sticky bottom-0 z-10 px-4 pb-3 pt-2 bg-app before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-8 before:bg-gradient-to-t before:from-app before:to-transparent before:content-['']">
          <div className="chat-column flex items-center">{messageInputArea}</div>
        </div>
      )}
      {isWelcomeScreen && (
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4" style={{ paddingTop: '80px', paddingBottom: '15vh' }}>
          <div className="text-center mb-10">
            <div className="mb-5 flex justify-center">
              {/* The welcome mark: three sheets — the corpus — fan open behind
                  the spark and square up again. Chosen over the sparkle-spin
                  family because browsing documents is what this product does;
                  the spark only annotates it. Styling and motion live in
                  elastiq.css (.welcome-pages), token-built so both themes and
                  prefers-reduced-motion (a still, squared stack) come free. */}
              <div className="welcome-pages" aria-hidden="true">
                <span className="welcome-page welcome-page--left" />
                <span className="welcome-page welcome-page--right" />
                <span className="welcome-page welcome-page--front" />
                <svg className="welcome-pages-spark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path className="welcome-star welcome-star--big" d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
                  <path className="welcome-star welcome-star--s1" d="M5 3v4M3 5h4"/>
                  <path className="welcome-star welcome-star--s2" d="M19 17v4M17 19h4"/>
                  <path className="welcome-star welcome-star--s3" d="M20 3v3M18.5 4.5h3"/>
                </svg>
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold mb-3 text-fg">
              {/* Greeted by first name, in the app's own gradient pair — the
                  same --grad-a/--grad-b the send button already wears, so the
                  page stays one palette. Falls back to the plain welcome when
                  the profile carries no name to greet with. */}
              {(props.user?.name || '').trim() && (
                <>
                  <span
                    style={{
                      background: 'linear-gradient(90deg, var(--grad-a), var(--grad-b))',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                    }}
                  >
                    Hi {props.user.name.trim().split(/\s+/)[0]}!
                  </span>{' '}
                </>
              )}
              {appConfig.brandFullName ? `Welcome to ${appConfig.brandFullName}` : 'Welcome'}
            </h2>
            <p className="text-[15px] text-fg-muted max-w-lg mx-auto">
              Ask me anything about your data and I'll provide insights and analytics.
            </p>
          </div>

          <div className="chat-column mb-6 flex items-center">
            {messageInputArea}
          </div>

          <div className="chat-column">
            <SuggestionCards onSelectPrompt={handleSuggestionClick} />
          </div>
        </div>
      )}

      <div className="relative">
        {!isWelcomeScreen && <div className="chat-message h-14"></div>}

        {props.isLoadingMessages && (
          <div className="flex justify-center items-center p-5 my-5">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {!props.isLoadingMessages &&
          visibleMessages
            .map((msg, index, filteredArray) => {
            const selectedOption = getSelectedOption(msg.id, msg.clarification_options);
            
            // Check if we need a date separator (compare with previous message in filtered array)
            const showDateSeparator = index === 0 || !isSameDay(
              filteredArray[index - 1].created_at,
              msg.created_at
            );

            // Hide bot card during streaming until answer_type arrives
            const isBotStreaming = isTyping && index === filteredArray.length - 1 && msg.author_id === 0;
            if (isBotStreaming && !msg.answer_type) {
              return null;
            }

            // The user turn this answer replies to — the Markdown/PDF exports
            // open with it. Walked back from the answer rather than taken from
            // parent_message_id, which is the QUOTED-reply link and null on a
            // normal turn.
            let questionText = null;
            if (msg.author_id === 0) {
              for (let qi = index - 1; qi >= 0; qi -= 1) {
                const prev = filteredArray[qi];
                if (prev.author_id !== 0 && typeof prev.message === 'string' && prev.message.trim()) {
                  questionText = prev.message.trim();
                  break;
                }
              }
            }

            return (
              <div key={`${msg.id}-${index}`}>
                {showDateSeparator && msg.created_at && (
                  <DateSeparator
                    date={formatDate(msg.created_at)}
                  />
                )}
                <ChatMessage
                  id={msg.id}
                  authorId={msg.author_id === 0 ? 0 : props.user.id}
                  author={msg.author_id === 0 ? "Elastiq" : props.user.name}
                  avatar={msg.author_id === 0 ? "/elastiq_logo.png" : (props.user.photoURL || null)}
                  message={msg.message}
                  source={msg.source}
                  description={msg.description}
                  totalRows={msg.total_rows}
                  dataSourcesMap={msg.data_sources_map}
                  datatype={msg.datatype || msg.type}
                  type={msg.type}
                  clarificationOptions={msg.clarification_options || []}
                  onClarificationOptionClick={handleClarificationOptionClick}
                  clarificationOptionsDisabled={usedClarificationMessages.has(msg.id)}
                  selectedClarificationOption={selectedOption}
                  question={questionText}
                  parentMessage={
                    msg.parent_message_id ? props.liveChat.find((message) => message.id === msg.parent_message_id) : null
                  }
                  resizeInput={resizeInput}
                  setQuotedMessage={setQuotedMessage}
                  setCurrentParentId={setCurrentParentId}
                  chatSessionId={props.activeChatSession}
                  setLiveChat={props.setLiveChat}
                  curl_commands={msg.curl_commands}
                  initialFeedback={msg.feedback || 0}
                  user={props.user}
                  timestamp={msg.created_at}
                  isStreaming={isTyping && index === filteredArray.length - 1 && msg.author_id === 0}
                  analysis_mode={msg.analysis_mode}
                  reference={msg.reference}
                  answer_type={msg.answer_type}
                  reasoning_steps={msg.reasoning_steps}
                  onOpenData={openDataRail}
                  isDataOpen={dataRail?.messageId === msg.id}
                />
              </div>
            );
          })}

        {/* Activity for the turn in flight. The bot card that normally hosts the
            timeline is deliberately hidden until answer_type arrives (see the
            gate above), which on a deployment whose stream is buffered means it
            appears only once everything is over. So while the turn runs, the
            timeline lives here in the same assistant column — px-3 matching the
            indicator and ChatMessage's p-3, so nothing shifts at the handoff. */}
        {isTyping && liveActivitySteps.length > 0 && (
          <div className="w-full px-3 pt-2">
            {/* Same column as the assistant message that replaces this one, so
                nothing shifts at the handoff — `chat-column` is the single
                definition both read. */}
            <div className="chat-column">
              <ActivityTimeline steps={liveActivitySteps} isStreaming isDarkMode={isDarkMode} />
            </div>
          </div>
        )}
        {/* Only until the timeline has something to show. The timeline's own
            header carries the spinner and the current activity, so keeping the
            indicator alongside it printed the same line twice. */}
        {isTyping && liveActivitySteps.length === 0 && (
          <ChatTypingIndicator statusMessage={streamingStatus} />
        )}
        {!isWelcomeScreen && <div className="h-36"></div>}
      </div>
    </div>
    {railView && (
      <DataRail
        view={railView}
        sessionId={props.activeChatSession}
        onSelectOutput={(sourceKey) => setDataRail((prev) => (prev ? { ...prev, sourceKey } : prev))}
        onClose={() => setDataRail(null)}
        isOverlay={railIsOverlay}
        width={railWidth}
        onResize={setRailWidth}
      />
    )}
    </div>
  );
}

ChatScreen.propTypes = {
  liveChat: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      author_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      message: PropTypes.string.isRequired,
      source: PropTypes.string,
      total_rows: PropTypes.number,
      type: PropTypes.string,
      parent_message_id: PropTypes.string,
      clarification_options: PropTypes.arrayOf(PropTypes.string),
      description: PropTypes.string,
      datatype: PropTypes.string,
      curl_commands: PropTypes.array,
      feedback: PropTypes.number,
      created_at: PropTypes.number, // Add timestamp to PropTypes
    })
  ).isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  activeChatSession: PropTypes.string,
  setLiveChat: PropTypes.func.isRequired,
  setChatSessions: PropTypes.func.isRequired,
  setActiveChatSession: PropTypes.func.isRequired,
  handleScroll: PropTypes.func,
  isSideBar: PropTypes.bool,
  updateSessionTimestamp: PropTypes.func.isRequired,
  isLoadingMessages: PropTypes.bool.isRequired,
  AgentSelector: PropTypes.elementType,
  chatSessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      summary: PropTypes.string,
    })
  ),
  newlyCreatedSessionsRef: PropTypes.object,
  emptySessionsRef: PropTypes.object,
  onSessionModeChange: PropTypes.func,
  sessionModes: PropTypes.object,
  onStreamStart: PropTypes.func,
  onStreamEnd: PropTypes.func,
  missingSessionParams: PropTypes.arrayOf(PropTypes.string),
  sessionCreationError: PropTypes.string,
};

export default ChatScreen;

